import { getConfiguredCmsOrigin, shouldUseCmsDevProxy } from './cms-origin';

const PRIVATE_IPV4_PATTERN =
  /^(?:10(?:\.\d{1,3}){3}|172\.(?:1[6-9]|2\d|3[0-1])(?:\.\d{1,3}){2}|192\.168(?:\.\d{1,3}){2})$/;

/**
 * Macht CMS-Asset-URLs abspielbar.
 *
 * Lokal schreibt der Vite-Proxy `/uploads/` auf `/__cms/api/uploads/` um.
 * In Produktion beantwortet nginx `/uploads/` selbst (statisch oder HTML-404)
 * und reicht nur `/api/uploads/` an die App weiter. Ein `<audio>`-Element
 * meldet das als NotSupportedError.
 */
export function resolveCmsDevProxyUrl(url: string | undefined | null): string | undefined | null {
  if (!url) return url;
  if (url.startsWith('data:') || url.startsWith('blob:')) return url;

  const playableUrl = toPlayableCmsUrl(url);
  if (shouldUseCmsDevProxy()) return toDevProxyUrl(playableUrl);
  return toCmsOriginUrl(playableUrl);
}

function toPlayableCmsUrl(url: string): string {
  return preferHttps(rewriteBareUploadsPath(url));
}

function rewriteBareUploadsPath(url: string): string {
  if (url.startsWith('/uploads/')) return `/api${url}`;

  const parsed = parseHttpUrl(url);
  if (!parsed || !parsed.pathname.startsWith('/uploads/')) return url;

  parsed.pathname = `/api${parsed.pathname}`;
  return parsed.toString();
}

function preferHttps(url: string): string {
  if (!url.startsWith('http://') || !isHttpsPage()) return url;

  const parsed = parseHttpUrl(url);
  if (!parsed || isLocalHostname(parsed.hostname)) return url;

  parsed.protocol = 'https:';
  return parsed.toString();
}

function isHttpsPage(): boolean {
  return typeof window !== 'undefined' && window.location.protocol === 'https:';
}

function toCmsOriginUrl(url: string): string {
  if (!isRootRelativeCmsPath(url)) return url;
  return `${getConfiguredCmsOrigin()}${url}`;
}

function isRootRelativeCmsPath(url: string): boolean {
  return url.startsWith('/api/') || url.startsWith('/uploads/') || url.startsWith('/temp/');
}

function toDevProxyUrl(url: string): string {
  if (url.startsWith('/__cms')) return url;
  if (url.startsWith('/api/')) return `/__cms${url}`;
  if (url.startsWith('/uploads/')) return `/__cms/api${url}`;

  const parsed = parseHttpUrl(url);
  if (!parsed) return url;
  if (!isLoopback(parsed) && !matchesViteCmsOrigin(parsed)) return url;

  const pathname = parsed.pathname.startsWith('/uploads/')
    ? `/api${parsed.pathname}`
    : parsed.pathname;
  return `/__cms${pathname}${parsed.search}${parsed.hash}`;
}

function parseHttpUrl(url: string): URL | null {
  if (!url.startsWith('http://') && !url.startsWith('https://')) return null;
  try {
    return new URL(url);
  } catch {
    return null;
  }
}

function isLoopback(parsed: URL): boolean {
  return parsed.hostname === '127.0.0.1' || parsed.hostname === 'localhost';
}

function isLocalHostname(hostname: string): boolean {
  const normalized = hostname.toLowerCase().replace(/^\[|\]$/g, '');
  if (normalized === 'localhost' || normalized === '127.0.0.1' || normalized === '::1') {
    return true;
  }
  if (normalized.endsWith('.local') || normalized.endsWith('.localhost')) return true;
  return PRIVATE_IPV4_PATTERN.test(normalized);
}

function matchesViteCmsOrigin(parsed: URL) {
  const raw = import.meta.env.VITE_CMS_ORIGIN;
  if (!raw) return false;
  try {
    const cms = new URL(raw);
    return parsed.protocol === cms.protocol && parsed.host === cms.host;
  } catch {
    return false;
  }
}
